import type { PlaceMatch, ZoneInput } from '@chukua/common';
import type { Map as MapLibreMap } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { ZONE_FIT_OPTIONS } from '../../../../../shared/features/maps/policies/map-framing';
import { useZoneDrawing } from './use-zone-drawing';
import { useZoneRecord } from './use-zone-record';
import { radiusForPlace } from '../policies/place-radius';
import { useZoneRadiusPreview } from './use-zone-radius-preview';
import { withRadius, type ZoneShapeValue } from '../policies/zone-draw-features';
import { zoneMapBounds } from '../../map/policies/zone-map-bounds';
import { createZone, deactivateZone, reactivateZone, updateZone, type Zone } from '../../../zones.api';

/** The fields the panel's form holds; the shape comes from the map. */
export type ZoneFields = Omit<ZoneInput, 'polygon' | 'centroid' | 'radiusMeters'>;

interface UseZonePanelArgs {
  zoneId: string;
  map: MapLibreMap | null;
  /** After any saved change, so the map and table show it. */
  onChanged: () => Promise<void>;
  onClose: () => void;
}

function saveErrorMessage(err: unknown): string {
  return err instanceof ApiError && err.status === 409 ? 'A zone with this code already exists.' : 'Could not save — check the fields and try again.';
}

function shapeOf(zone: Zone | null): ZoneShapeValue {
  return { polygon: zone?.polygon ?? null, centroid: zone?.centroid ?? null, radiusMeters: zone?.radiusMeters ?? null };
}

/**
 * One zone's panel on the Zones map: the record, its shape as drawn so far, terra-draw on the shared map
 * while the panel is open, and the save and status actions. A save toasts and closes the panel.
 */
export function useZonePanel({ zoneId, map, onChanged, onClose }: UseZonePanelArgs) {
  const record = useZoneRecord(zoneId);
  const zone = record.state.status === 'ready' ? record.state.zone : null;
  // Null until the admin changes the shape; until then the saved one stands.
  const [drawnShape, setDrawnShape] = useState<ZoneShapeValue | null>(null);
  const shape = drawnShape ?? shapeOf(zone);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const drawing = useZoneDrawing({
    map,
    drawingKey: record.state.status === 'ready' ? zoneId : null,
    initialValue: () => shape,
    onChange: (geometry) => setDrawnShape(withRadius(geometry, shape.radiusMeters)),
  });
  useZoneRadiusPreview(map, shape);

  // Framed once, when the zone first loads: not again as its shape is edited.
  const savedBoundsRef = useRef(zone ? zoneMapBounds(zone) : null);
  useEffect(() => {
    savedBoundsRef.current = zone ? zoneMapBounds(zone) : null;
  });
  const loadedZoneId = zone?.id;
  useEffect(() => {
    if (map && savedBoundsRef.current) map.fitBounds(savedBoundsRef.current, { ...ZONE_FIT_OPTIONS, duration: 0 });
  }, [map, loadedZoneId]);

  async function save(fields: ZoneFields): Promise<void> {
    setSaveError(null);
    setSaving(true);
    try {
      await (zone ? updateZone(zone.id, { ...fields, ...shape }) : createZone({ ...fields, ...shape }));
    } catch (err) {
      setSaveError(saveErrorMessage(err));
      setSaving(false);
      return;
    }
    setSaving(false);
    toast.success(`${fields.name} ${zone ? 'updated' : 'created'}`);
    await onChanged();
    onClose();
  }

  async function setActive(isActive: boolean): Promise<void> {
    if (!zone) return;
    try {
      await (isActive ? reactivateZone(zone.id) : deactivateZone(zone.id));
    } catch {
      toast.error(`Couldn't ${isActive ? 'reactivate' : 'deactivate'} ${zone.name}`);
      return;
    }
    await Promise.all([record.reload(), onChanged()]);
    toast.success(`${zone.name} ${isActive ? 'reactivated' : 'deactivated'}`);
  }

  /**
   * Starts the zone from a place picked by name: a point at its centre, reaching as far as the place does,
   * with the map on it. The admin then tunes the radius, or traces a polygon instead.
   */
  function startFromPlace(place: PlaceMatch) {
    const placed: ZoneShapeValue = { polygon: null, centroid: { type: 'Point', coordinates: place.center }, radiusMeters: radiusForPlace(place) };
    drawing.loadShape(placed);
    setDrawnShape(placed);
    const bounds = zoneMapBounds(placed);
    if (map && bounds) map.fitBounds(bounds, { ...ZONE_FIT_OPTIONS, duration: 0 });
  }

  /** A point zone's reach, typed in the panel; the circle on the map follows it. */
  const setRadius = (radiusMeters: number | null) => setDrawnShape({ ...shape, radiusMeters });

  return { record: record.state, zone, shape, drawing, setRadius, startFromPlace, saveError, saving, save, setActive };
}
